import { ChangeEvent, FC, KeyboardEvent, memo, useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';
import classNames from 'classnames';

import { GProduct } from '@/types';
import {
  Button,
  Close,
  Heading,
  Icon,
  IconSvg,
  Input,
  Link,
  Product,
  ProductPlaceholder,
  Slider,
  useData,
} from '@/components';
import { useActiveHeaderSlice } from '@/hooks';
import { PAGE_ROUTES } from '@/constants';
import { useUiStoreSelector } from '@/data';

import { useSearchProducts, useSearchTerm } from './context';

import styles from './searchheader.module.scss';

const ENTER_KEY = 'Enter';

export const CustomSearchInput = memo(
  ({
    autoFocus,
    inlineNav,
    onClose,
  }: {
    autoFocus?: boolean;
    inlineNav?: boolean;
    onClose?: () => void;
  }) => {
    const activeHeader = useActiveHeaderSlice();
    const { setState } = useUiStoreSelector(['is_search_open']);
    const { searchTerm, updateSearchTerm } = useSearchTerm();
    const router = useRouter();
    const searchBoxRef = useRef<HTMLInputElement>(null);
    const isDefaultHeader =
      !inlineNav && (activeHeader?.variation ?? 'default') === 'default';

    const handleFocus = () => {
      setState({ is_search_open: true });
    };

    const handleEnterKeyPress = (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setState({ is_search_open: false });
        onClose?.();
        return;
      }

      setState({ is_search_open: true });
      if (event.key === ENTER_KEY) {
        setState({ is_search_open: false });
        searchBoxRef?.current?.blur();
        router.push(`${PAGE_ROUTES.SHOP}?query=${searchTerm}`, {
          scroll: false,
        });
      }
    };

    const handleSearch = () => {
      if (searchTerm !== '') {
        setState({ is_search_open: false });
        router.push(`${PAGE_ROUTES.SHOP}?query=${searchTerm}`, {
          scroll: false,
        });
      }
    };

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      const value = e.currentTarget.value;
      updateSearchTerm(value);
    };

    useEffect(() => {
      if (!autoFocus) return;

      setState({ is_search_open: true });

      const frame = requestAnimationFrame(() => {
        searchBoxRef.current?.focus();
      });

      return () => cancelAnimationFrame(frame);
    }, [autoFocus, setState]);

    return (
      <div
        className={classNames(styles.search_box, {
          [styles.search_box__default]: isDefaultHeader,
          [styles.search_box__inline_nav]: inlineNav,
        })}
      >
        <Input
          aria-label="Search products"
          autoComplete="off"
          className={styles.search_box__input}
          isSmall
          name={'searchBox'}
          onChange={handleChange}
          onFocus={handleFocus}
          onKeyDown={handleEnterKeyPress}
          placeholder="Search"
          ref={searchBoxRef}
          type="search"
          value={searchTerm}
        />
        <Button
          aria-label={
            searchTerm ? `Search for "${searchTerm}"` : 'Search for products'
          }
          className={styles.search_box__button}
          color="primary"
          onClick={() => handleSearch()}
          type="button"
        >
          <IconSvg
            decorative={false}
            name="search"
            title={
              searchTerm ? `Search for "${searchTerm}"` : 'Search for products'
            }
          />
        </Button>
      </div>
    );
  },
);

export const CustomSearchBox = memo(() => {
  const { setState } = useUiStoreSelector(['is_search_open']);
  const { searchTerm, updateSearchTerm } = useSearchTerm();
  const router = useRouter();

  const handleEnterKeyPress = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === ENTER_KEY) {
      setState({ is_search_open: false });
      router.push(`${PAGE_ROUTES.SHOP}?query=${searchTerm}`, {
        scroll: false,
      });
    }
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value;
    updateSearchTerm(value);
  };

  return (
    <div className={styles.search__header}>
      <div className={styles.search__header__box}>
        <input
          aria-label="Search products"
          autoComplete="off"
          autoFocus
          className={styles.search__header__box__input}
          name="searchBox"
          onChange={handleChange}
          onKeyDown={handleEnterKeyPress}
          placeholder="Search"
          type="search"
          value={searchTerm}
        />
        <Close
          aria-label="Close search"
          onClick={() => setState({ is_search_open: false })}
        />
      </div>
    </div>
  );
});

export const CustomSearchIcon = ({ onClick }: { onClick?: () => void }) => {
  const { setState } = useUiStoreSelector([]);

  return (
    <button
      aria-label="Open search dialog"
      className={styles.search__icon}
      onClick={() => {
        if (onClick) {
          onClick();
          return;
        }

        setState({ is_search_open: true });
      }}
      type="button"
    >
      <IconSvg decorative={false} name="search" title="Open search dialog" />
    </button>
  );
};

interface ISearchBodyProps {
  seeAllHref: string;
  closeButtonHidden?: boolean;
  inlineMode?: boolean;
}

export const SearchResultsBody: FC<ISearchBodyProps> = ({
  seeAllHref,
  closeButtonHidden,
  inlineMode,
}) => {
  const { setState } = useUiStoreSelector(['is_search_open']);
  const { products, isLoadingProducts } = useSearchProducts();
  const { searchTerm } = useSearchTerm();

  return (
    <div
      className={classNames(styles['search__results-body'], {
        [styles.embedded_input]: closeButtonHidden,
      })}
    >
      {(!closeButtonHidden || inlineMode) && (
        <Close
          aria-label="Close search"
          className={styles.search__body_action}
          onClick={() => setState({ is_search_open: false })}
        />
      )}
      {!products?.length && !isLoadingProducts ? (
        <div className={styles.empty_results}>
          <Icon aria-hidden="true" name="search" />
          <Heading level={5}>No search results found</Heading>
          <p>
            {`No results match the criteria ${
              searchTerm !== '' && `( ${searchTerm} )`
            }. Remove criteria to search again`}
          </p>
        </div>
      ) : (
        <div className={styles.search__results}>
          <SearchIndex
            isLoadingProducts={isLoadingProducts}
            products={products}
            seeAllHref={seeAllHref}
          />
        </div>
      )}
    </div>
  );
};

const SearchIndex = ({
  seeAllHref,
  products,
  isLoadingProducts = false,
}: {
  seeAllHref: string;
  products: GProduct[];
  isLoadingProducts?: boolean;
}) => {
  const { searchTerm } = useSearchTerm();
  const { is_search_open, setState } = useUiStoreSelector(['is_search_open']);

  return (
    <div className={styles.search__hits}>
      <CustomHits hits={products} isLoading={isLoadingProducts} />
      <div className={styles['search__hits-header']}>
        <Heading className={styles['heading-2']} level={2}>
          Search Result
        </Heading>
        <Link
          aria-label={`Go to shop all results for ${
            searchTerm || 'all products'
          }`}
          className={styles['products__see-all']}
          href={`${seeAllHref ?? 'deals'}?query=${searchTerm}`}
          onClick={() =>
            setState({
              is_search_open: !is_search_open,
            })
          }
          shallow
        >
          Shop All
        </Link>
      </div>
    </div>
  );
};

const CustomHits = ({
  hits,
  isLoading,
}: {
  hits: GProduct[];
  isLoading: boolean;
}) => {
  const { productCard } = useData();
  const {
    breakpoints,
    spacingBetweenCard,
    defaultSlidePerView,
    defaultSpaceBetween,
    isLargeCard,
  } = productCard.displayProps;

  return (
    <Slider
      activatedHover
      ariaLabel="Search results carousel"
      breakpoints={breakpoints}
      centeredSlides={false}
      freeMode
      isNotPaddingMobile={spacingBetweenCard === 'none'}
      isSliderProductCard
      showSliderHeader={false}
      showSlidesOutsideContainer
      slidesPerView={defaultSlidePerView}
      spaceBetween={defaultSpaceBetween}
    >
      {isLoading
        ? new Array(8)
            .fill(null)
            .map((_, index) => (
              <ProductPlaceholder key={index} largeCard={isLargeCard} />
            ))
        : hits.map((item, index) => {
            return (
              <Product
                className={classNames(styles['search_product-card'], {
                  [styles['search_product-card-large']]: isLargeCard,
                })}
                key={index}
                product={item}
              />
            );
          })}
    </Slider>
  );
};
